import { code } from "@spscommerce/utils";

export const SpsFormExamples = {
    a_basic: {
        label: "Basic Usage",
        description: "",
        examples: {
            basic: {
                react: code`
                    function DemoComponent() {
                        const COLOR_OPTIONS = ["red", "blue", "green"];
                        const initValue = {
                            name: "Foo",
                            color: COLOR_OPTIONS[0],
                        };
                        const { formValue, formMeta, updateForm } = useSpsForm(initValue, {
                            "name": value => value.color === COLOR_OPTIONS[0] ? [SpsValidators.required] : []
                        });

                        function handleSubmit() {
                            console.log("submit", formValue);
                        }
                        function reset() {
                            updateForm(initValue);
                        }

                        return <>
                            <SpsForm formMeta={formMeta} onSubmit={handleSubmit}>
                                <div className="sfg-row">
                                    <div className="sfg-col-8">
                                        <SpsLabel for={formMeta.fields.name}>Name</SpsLabel>
                                        <SpsTextInput
                                            value={formValue.name}
                                            formMeta={formMeta.fields.name}
                                        />
                                    </div>
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.color}>Color</SpsLabel>
                                        {COLOR_OPTIONS.map(color =>
                                            <SpsRadioButton key={color} name={color} value={color} label={color}
                                                checked={color === formValue.color}
                                                formMeta={formMeta.fields.color}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="sfg-row">
                                    <div className="sfg-col-12">
                                        <SpsButton className="mr-1" onClick={reset}>Reset</SpsButton>
                                        <SpsButton type={ButtonType.SUBMIT} kind={ButtonKind.CONFIRM}>Submit</SpsButton>
                                    </div>
                                </div>
                            </SpsForm>
                            <br/>
                            {JSON.stringify(formValue)}
                        </>;
                    }
                `
            }
        }
    },
    b_array: {
        label: "Form Array",
        description: "",
        examples: {
            array: {
                react: code`
                function DemoComponent() {
                    const initValue = {
                      itemList: ["foo"],
                    }
                    const { formValue, formMeta, updateForm } = useSpsForm(initValue, {
                      "itemList.*": [SpsValidators.required],
                    })

                    function handleSubmit() {
                      console.log("submit", formValue)
                    }
                    function reset() {
                      updateForm(initValue)
                    }

                    function addItem() {
                      updateForm({
                        ...formValue,
                        itemList: [...formValue.itemList, ""],
                      })
                    }
                    function removeItem(index: number) {
                      updateForm({
                        ...formValue,
                        itemList: formValue.itemList.filter((_, i) => index !== i),
                      })
                    }

                    return (
                      <>
                        <SpsForm formMeta={formMeta} onSubmit={handleSubmit}>
                          {formValue.itemList.map((item, index) => (
                            <div className="sfg-row" key={index}>
                              <div className="sfg-col-4">
                                <SpsLabel for={formMeta.fields.itemList.fields[index]}>
                                  Item
                                </SpsLabel>
                                <SpsTextInput
                                  value={item}
                                  formMeta={formMeta.fields.itemList.fields[index]}
                                />
                              </div>
                              <div className="sfg-col-1">
                                <SpsButton
                                  kind={ButtonKind.ICON}
                                  icon={SpsIcon.TRASH}
                                  className="sps-label-spacing"
                                  onClick={() => removeItem(index)}
                                />
                              </div>
                            </div>
                          ))}
                          <div className="sfg-row">
                            <div className="sfg-col-12">
                              <SpsButton
                                className="mr-1"
                                kind={ButtonKind.KEY}
                                icon={SpsIcon.PLUS_SIGN}
                                onClick={addItem}
                              >
                                Add Item
                              </SpsButton>
                              <SpsButton className="mr-1" onClick={reset}>
                                Reset
                              </SpsButton>
                              <SpsButton
                                type={ButtonType.SUBMIT}
                                kind={ButtonKind.CONFIRM}
                              >
                                Submit
                              </SpsButton>
                            </div>
                          </div>
                        </SpsForm>
                        <br />
                        {JSON.stringify(formValue)}
                      </>
                    )
                  }
                `
            }
        }
    },
    c_dynamic_group: {
        label: "Dynamic Group",
        description: "",
        examples: {
            basic: {
                react: code`
                function DemoComponent() {
                    interface FormValueType {
                      name: string
                      other_stuff?: {
                        tagline: string
                        description: string
                      }
                    }
                    const initValue: FormValueType = {
                      name: "Foo",
                    }
                    const { formValue, formMeta, updateForm } = useSpsForm(initValue)

                    function handleFieldsetToggle(enabled: boolean) {
                      if (enabled) {
                        updateForm({
                          ...formValue,
                          other_stuff: {
                            tagline: "",
                            description: "",
                          },
                        })
                      } else {
                        updateForm(omit(formValue, "other_stuff"))
                      }
                    }

                    function handleSubmit() {
                      console.log("submit", formValue)
                    }
                    function reset() {
                      updateForm(initValue)
                    }

                    return (
                      <>
                        <SpsForm formMeta={formMeta} onSubmit={handleSubmit}>
                          <div className="sfg-row">
                            <div className="sfg-col-8">
                              <SpsLabel for={formMeta.fields.name}>Name</SpsLabel>
                              <SpsTextInput
                                value={formValue.name}
                                formMeta={formMeta.fields.name}
                              />
                            </div>
                            <div className="sfg-col-4"></div>
                          </div>
                          <div className="sfg-row">
                            <div className="sfg-col-12">
                              <SpsFieldset
                                optional
                                enabled={formMeta.fields.other_stuff}
                                formMeta={formMeta.fields.other_stuff}
                                onToggled={handleFieldsetToggle}
                                legend="Other Stuff"
                              >
                                {formMeta.fields.other_stuff && (
                                  <>
                                    <div className="sfg-row">
                                      <div className="sfg-col-12">
                                        <SpsLabel
                                          for={formMeta.fields.other_stuff.fields.tagline}
                                        >
                                          Tagline
                                        </SpsLabel>
                                        <SpsTextInput
                                          value={formValue.other_stuff.tagline}
                                          formMeta={formMeta.fields.other_stuff.fields.tagline}
                                        />
                                      </div>
                                    </div>
                                    <div className="sfg-row">
                                      <div className="sfg-col-12">
                                        <SpsLabel
                                          for={formMeta.fields.other_stuff.fields.description}
                                        >
                                          Description
                                        </SpsLabel>
                                        <SpsTextInput
                                          value={formValue.other_stuff.description}
                                          formMeta={
                                            formMeta.fields.other_stuff.fields.description
                                          }
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </SpsFieldset>
                            </div>
                          </div>
                          <div className="sfg-row">
                            <div className="sfg-col-12">
                              <SpsButton className="mr-1" onClick={reset}>
                                Reset
                              </SpsButton>
                              <SpsButton
                                type={ButtonType.SUBMIT}
                                kind={ButtonKind.CONFIRM}
                              >
                                Submit
                              </SpsButton>
                            </div>
                          </div>
                        </SpsForm>
                        <br />
                        {JSON.stringify(formValue)}
                      </>
                    )
                  }
                `
            }
        }
    },
};
